import { areOccupancyArraysEqual } from 'lib/offer/occupancyUtils'
import {
  safeStorageGetItem,
  safeStorageRemoveItem,
  safeStorageSetItem,
} from 'lib/search/typeaheadConfirmationStorage'

const OCCUPANCY_CONFIRMATION_STORAGE_PREFIX = 'nl-occupancy-confirmation'
const OCCUPANCY_ACK_STORAGE_PREFIX = 'nl-occupancy-acknowledgement'

export type OccupancyConfirmationMutableState = Pick<App.TypeaheadSubmitRefData,
  | 'occupancyConfirmationBaseline'
  | 'occupancyConfirmationAppliedSelection'
  | 'occupancyConfirmationAcknowledgedSelection'
  | 'occupancyConfirmationRequired'
  | 'occupancyConfirmationViewed'
>

function occupancyConfirmationKey(typeaheadId: string): string {
  return `${OCCUPANCY_CONFIRMATION_STORAGE_PREFIX}:${typeaheadId}`
}

function occupancyAcknowledgementKey(typeaheadId: string): string {
  return `${OCCUPANCY_ACK_STORAGE_PREFIX}:${typeaheadId}`
}

/**
 * Marks the current applied extract as acknowledged so the soft-confirm prompt
 * is not shown again for the same occupancy.
 */
export function acknowledgeOccupancyConfirmation(
  refData: OccupancyConfirmationMutableState | null | undefined,
): void {
  if (!refData) return
  refData.occupancyConfirmationViewed = true
  refData.occupancyConfirmationAcknowledgedSelection = refData.occupancyConfirmationAppliedSelection
}

/** Acknowledge and clear the pending requirement — the user has dealt with the change. */
export function completeOccupancyConfirmation(
  refData: OccupancyConfirmationMutableState | null | undefined,
): void {
  if (!refData) return
  acknowledgeOccupancyConfirmation(refData)
  refData.occupancyConfirmationRequired = false
}

/**
 * Starts a fresh confirmation pass, e.g. when the NL phrase changes incompatibly.
 */
export function resetOccupancyConfirmationState(
  refData: OccupancyConfirmationMutableState,
  baseline: Array<App.Occupants> | undefined,
): void {
  refData.occupancyConfirmationBaseline = baseline
  refData.occupancyConfirmationAppliedSelection = undefined
  refData.occupancyConfirmationAcknowledgedSelection = undefined
  refData.occupancyConfirmationRequired = false
  refData.occupancyConfirmationViewed = false
}

/**
 * Updates soft-confirm flags for an extract that changes the form occupancy.
 * Returns whether the caller should notify that confirmation is required.
 */
export function updateOccupancyConfirmationStateOnExtract(
  refData: OccupancyConfirmationMutableState | null | undefined,
  {
    currentOccupancies,
    extractedOccupancies,
  }: {
    currentOccupancies: Array<App.Occupants> | undefined
    extractedOccupancies: Array<App.Occupants>
  },
): boolean {
  if (!refData) return false

  // Same extracted occupancy already shown once — never re-prompt for it.
  if (areOccupancyArraysEqual(refData.occupancyConfirmationAcknowledgedSelection, extractedOccupancies)) {
    refData.occupancyConfirmationAppliedSelection = extractedOccupancies
    refData.occupancyConfirmationRequired = false
    refData.occupancyConfirmationViewed = true
    return false
  }

  if (!refData.occupancyConfirmationBaseline) {
    refData.occupancyConfirmationBaseline = currentOccupancies
  }

  const previousAppliedSelection = refData.occupancyConfirmationAppliedSelection
  refData.occupancyConfirmationAppliedSelection = extractedOccupancies
  const needsConfirmation = !areOccupancyArraysEqual(refData.occupancyConfirmationBaseline, extractedOccupancies)
  refData.occupancyConfirmationRequired = needsConfirmation

  if (!needsConfirmation) {
    refData.occupancyConfirmationViewed = false
    return false
  }

  // Each new extracted occupancy change needs its own confirmation pass.
  if (!areOccupancyArraysEqual(previousAppliedSelection, extractedOccupancies)) {
    refData.occupancyConfirmationViewed = false
  }

  return !refData.occupancyConfirmationViewed
}

export function getTypeaheadOccupancyConfirmationBaseline(
  typeaheadId: string | undefined,
): Array<App.Occupants> | undefined {
  if (!typeaheadId) return undefined

  const value = safeStorageGetItem(occupancyConfirmationKey(typeaheadId))
  if (!value) return undefined

  try {
    return JSON.parse(value) as Array<App.Occupants>
  } catch {
    return undefined
  }
}

export function markTypeaheadOccupancyAcknowledgementRequired(typeaheadId: string | undefined) {
  if (!typeaheadId) return
  safeStorageSetItem(occupancyAcknowledgementKey(typeaheadId), 'true')
}

/**
 * Persist confirmation state before navigating for an in-flight NL extract.
 * If the user already reviewed occupancy on the form, align the stored baseline with
 * the form occupancy and clear any pending results-page acknowledgement.
 * Otherwise keep the pre-extract baseline so the results page can soft-ack.
 */
export function persistTypeaheadOccupancyConfirmationForNavigation(
  refData: Pick<App.TypeaheadSubmitRefData, 'typeaheadId' | 'occupancyConfirmationViewed' | 'occupancyConfirmationBaseline'>,
  formOccupancies: Array<App.Occupants>,
) {
  const { typeaheadId, occupancyConfirmationViewed, occupancyConfirmationBaseline } = refData
  if (!typeaheadId) return

  safeStorageSetItem(
    occupancyConfirmationKey(typeaheadId),
    JSON.stringify(occupancyConfirmationViewed ? formOccupancies : occupancyConfirmationBaseline ?? formOccupancies),
  )
  if (occupancyConfirmationViewed) {
    safeStorageRemoveItem(occupancyAcknowledgementKey(typeaheadId))
  }
}

export function consumeTypeaheadOccupancyAcknowledgement(typeaheadId: string | undefined): boolean {
  if (!typeaheadId) return false

  const key = occupancyAcknowledgementKey(typeaheadId)
  const shouldAcknowledge = safeStorageGetItem(key) === 'true'
  safeStorageRemoveItem(key)
  return shouldAcknowledge
}
